export const CHAPTERS = [
  [
    'people',
    'Your people',
    'A plan for the people who count.',
    'Start with you, then include your partner, children and anyone you financially support.',
  ],
  [
    'income',
    'Your income',
    'What comes in makes things possible.',
    'Bring together regular income. We’ll ask about payroll deductions only for salary.',
  ],
  [
    'spending',
    'Your spending',
    'Make room for everyday life.',
    'Separate housing, essentials and annual costs. Rough estimates are a useful starting point.',
  ],
  [
    'balance',
    'What you own & owe',
    'See the picture you’ve already built.',
    'Include savings, investments and outstanding loans. Current values are enough.',
  ],
  [
    'protection',
    'Your protection',
    'Look after what matters.',
    'Tell us about existing cover and who it protects. It’s fine if you need to check a policy.',
  ],
  [
    'future',
    'Your future',
    'Give your money something to work towards.',
    'A home, education, retirement or something entirely your own. Start with what you know.',
  ],
]
export const ANNUAL_COSTS = [
  ['vacation', 'Travel and holidays', 'Trips and breaks across the year.'],
  [
    'childrenFees',
    'Current school or college fees',
    'All your children together, including tuition, coaching and activities.',
  ],
  ['carInsurance', 'Vehicle insurance', 'Annual premiums for all vehicles.'],
  ['medicalPremium', 'Health insurance premiums', 'What you pay each year, not the sum insured.'],
  ['termPremium', 'Term insurance premiums', 'What you pay each year for life cover.'],
  ['otherInsurance', 'Other insurance premiums', 'For example, home insurance.'],
]
let sequence = 0
export const newItem = (section, relationship = '') => ({
  id: `${Date.now()}-${sequence++}`,
  ...{
    members: { name: '', relationship, dob: '', occupation: '', dependent: '' },
    salaries: { member: '', incomeType: 'Salary', gross: '', tax: '', pf: '', nps: '' },
    holdings: { name: 'Mutual funds', value: '', location: '' },
    loans: { type: 'Home loan', amount: '', emi: '', monthsLeft: '', details: '' },
    assetGoals: {
      kind: 'Other',
      name: '',
      year: '',
      amount: '',
      saved: '',
      child: '',
      studyLocation: '',
    },
    extraYearlyExpenses: { name: '', amount: '' },
  }[section],
})
export const createPlanner = () => ({
  version: 2,
  schemaVersion: 3,
  questionnaire2Version: 2,
  members: [newItem('members', 'Self')],
  salaries: [],
  holdings: [],
  loans: [],
  assetGoals: [],
  extraYearlyExpenses: [],
  monthlyExpenses: { housing: '', household: '', rent: '', emi: '', parents: '', children: '' },
  yearlyExpenses: { ...Object.fromEntries(ANNUAL_COSTS.map(([key]) => [key, ''])), lifestyle: '' },
  annualSelected: [],
  annualChoice: '',
  parentsSupport: '',
  insurance: {
    corporateMedical: '',
    privateMedical: '',
    term: '',
    corporateCoverage: '',
    privateCoverage: '',
    termCoverage: '',
    corporateWho: '',
    privateWho: '',
    termWho: '',
    privateBasis: '',
    corporateBasis: '',
  },
  retirement: {
    monthly: '',
    style: '',
    estimateConfirmed: false,
    // Keep the source planner's three independent retirement streams. `monthly`
    // remains only as a fallback for plans created before this structure existed.
    basicCurrent: '',
    basicIncrease: 0,
    vacation: '',
    annual: '',
    monthlyIncrease: 0,
    vacationIncrease: 0,
    annualIncrease: 0,
  },
  childPlans: {},
  assumptions: {
    planTill: 90,
    equityReturns: 12,
    debtReturns: 7,
    essentialInflation: 6,
    nonEssentialInflation: 6,
    educationInflation: 8,
    realEstateInflation: 5,
    inflationVersion: 2,
    sipYearlyIncrease: 8,
    emergencyReserve: '',
    contingencyReserve: '',
    assetReplacementReserve: '',
  },
  estimates: null,
  answers: {},
})
export const hasValue = (value) => value !== '' && value !== undefined && value !== null
export const money = (value) =>
  hasValue(value)
    ? `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
    : 'Not answered'
export const lifestyleEstimate = (planner) =>
  Math.ceil((Number(planner.monthlyExpenses.household) || 0) / 50000) * 50000
export const retirementEstimate = (planner, style) => {
  const base =
    (Number(planner.monthlyExpenses.household) || 0) +
    Number(planner.yearlyExpenses.lifestyle || lifestyleEstimate(planner)) / 12 +
    (Number(planner.yearlyExpenses.vacation) || 0) / 12
  return Math.round((base * ({ Frugal: 0.8, Maintain: 1, Premium: 1.2 }[style] || 1)) / 100) * 100
}

const numberValue = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0)

// This mirrors the retirement inputs in the original Financial Planner source.
// Its values are derived here so that the form, review and estimate all use one
// definition of each stream.
export const retirementStreams = (planner) => {
  const selectedAnnualKeys = new Set(planner.annualSelected || [])
  const annualExpenseTotal =
    Object.entries(planner.yearlyExpenses || {}).reduce(
      (sum, [key, value]) =>
        sum + (key === 'lifestyle' || selectedAnnualKeys.has(key) ? numberValue(value) : 0),
      0,
    ) + (planner.extraYearlyExpenses || []).reduce((sum, item) => sum + numberValue(item.amount), 0)
  const sourceBasicExpense =
    numberValue(planner.monthlyExpenses?.household) + numberValue(planner.monthlyExpenses?.rent)
  const sourceVacationExpense = selectedAnnualKeys.has('vacation')
    ? numberValue(planner.yearlyExpenses?.vacation)
    : 0
  const parentAnnualExpense = (planner.extraYearlyExpenses || [])
    .filter((item) => /parent/i.test(item.name || item.label || ''))
    .reduce((sum, item) => sum + numberValue(item.amount), 0)
  const sourceAnnualExpense = Math.max(
    0,
    annualExpenseTotal - sourceVacationExpense - parentAnnualExpense,
  )
  const retirement = planner.retirement || {}
  const basicCurrent = hasValue(retirement.basicCurrent)
    ? numberValue(retirement.basicCurrent)
    : sourceBasicExpense || numberValue(retirement.monthly)
  const vacationCurrent = hasValue(retirement.vacation)
    ? numberValue(retirement.vacation)
    : sourceVacationExpense
  const annualCurrent = hasValue(retirement.annual)
    ? numberValue(retirement.annual)
    : sourceAnnualExpense
  const basicIncrease = numberValue(retirement.basicIncrease ?? retirement.monthlyIncrease)
  const vacationIncrease = numberValue(retirement.vacationIncrease)
  const annualIncrease = numberValue(retirement.annualIncrease)
  return {
    annualExpenseTotal,
    parentAnnualExpense,
    sourceBasicExpense,
    sourceVacationExpense,
    sourceAnnualExpense,
    basicCurrent,
    basicIncrease,
    basicPlanned: basicCurrent * (1 + basicIncrease / 100),
    vacationCurrent,
    vacationIncrease,
    vacationPlanned: vacationCurrent * (1 + vacationIncrease / 100),
    annualCurrent,
    annualIncrease,
    annualPlanned: annualCurrent * (1 + annualIncrease / 100),
  }
}

const monthYear = (date) => date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })

export const ageFromDob = (dob, today = new Date()) => {
  if (!dob) return null
  const date = new Date(`${dob}T00:00:00`)
  if (Number.isNaN(date.getTime())) return null
  let age = today.getFullYear() - date.getFullYear()
  const month = today.getMonth() - date.getMonth()
  if (month < 0 || (month === 0 && today.getDate() < date.getDate())) age -= 1
  return age >= 0 ? age : null
}

export const CHILD_EDUCATION_DEFAULTS = [
  { key: 'earlyYears', label: '0-4 Years', startAge: 0, endAge: 5, annualAmount: 200000 },
  { key: 'school', label: '5-18 years', startAge: 5, endAge: 19, annualAmount: 500000 },
  { key: 'undergrad', label: '19-22', startAge: 19, endAge: 23, annualAmount: 700000 },
  { key: 'grad', label: '23-24', startAge: 23, endAge: 25, annualAmount: 1500000 },
]

export const childEducationStage = (age) =>
  CHILD_EDUCATION_DEFAULTS.find((stage) => age >= stage.startAge && age < stage.endAge)

export const childEducationRemainingStages = (age) =>
  age === null
    ? CHILD_EDUCATION_DEFAULTS
    : CHILD_EDUCATION_DEFAULTS.filter((stage) => Math.floor(age) < stage.endAge)

export const defaultChildPlan = (childId, planner = {}, childAge = null) => {
  const schoolFees = numberValue(planner.yearlyExpenses?.childrenFees)
  const currentStage = childAge === null ? null : childEducationStage(childAge)
  return {
    childId,
    specialNeeds: false,
    educationInflation: 8,
    growth: 10,
    studyLocation: 'India',
    gradLocation: 'India',
    weddingAge: 27,
    weddingToday: '3000000',
    weddingInflation: 6,
    feesMappedStage: currentStage?.key || '',
    stages: Object.fromEntries(
      CHILD_EDUCATION_DEFAULTS.map((stage) => [
        stage.key,
        String(stage.key === currentStage?.key && schoolFees > 0 ? schoolFees : stage.annualAmount),
      ]),
    ),
  }
}

export const childPlanWithDefaults = (plan, childId, planner = {}, childAge = null) => {
  const base = defaultChildPlan(childId, planner, childAge)
  const currentStage = childAge === null ? null : childEducationStage(childAge)
  const schoolFees = numberValue(planner.yearlyExpenses?.childrenFees)
  const stages = { ...base.stages, ...(plan?.stages || {}) }
  if (currentStage && schoolFees > 0 && plan?.feesMappedStage !== currentStage.key) {
    for (const stage of CHILD_EDUCATION_DEFAULTS) {
      if (stage.key !== currentStage.key && Number(stages[stage.key]) === schoolFees) {
        stages[stage.key] = String(stage.annualAmount)
      }
    }
    stages[currentStage.key] = String(schoolFees)
  }
  return {
    ...base,
    ...(plan || {}),
    feesMappedStage: currentStage?.key || plan?.feesMappedStage || '',
    stages,
  }
}

const corpusForChildEducation = (plan, age) => {
  if (age === null || plan.specialNeeds) return 0
  const educationInflation = numberValue(plan.educationInflation) || 8
  const growth = numberValue(plan.growth) || 10
  let corpus = 0
  for (const stage of CHILD_EDUCATION_DEFAULTS) {
    const annualSpend = numberValue(plan.stages?.[stage.key])
    // The source planner funds the current education year as well as every
    // remaining year in the stage. Keeping that convention makes the source
    // and First Ideas figures comparable.
    const start = Math.max(Math.floor(age), stage.startAge)
    if (annualSpend <= 0 || start >= stage.endAge) continue
    for (let childAge = start; childAge < stage.endAge; childAge += 1) {
      const yearsFromNow = Math.max(0, childAge - age)
      const inflatedSpend = annualSpend * (1 + educationInflation / 100) ** yearsFromNow
      corpus += inflatedSpend / (1 + growth / 100) ** yearsFromNow
    }
  }
  return corpus
}

const corpusForChildWedding = (plan, age) => {
  if (age === null || plan.specialNeeds) return 0
  const weddingAge = numberValue(plan.weddingAge) || 27
  const weddingToday = numberValue(plan.weddingToday)
  if (weddingToday <= 0 || weddingAge <= age) return 0
  const yearsFromNow = weddingAge - age
  const inflation = numberValue(plan.weddingInflation) || 6
  const growth = numberValue(plan.growth) || 10
  return (weddingToday * (1 + inflation / 100) ** yearsFromNow) / (1 + growth / 100) ** yearsFromNow
}

const corpusForRecurringCost = (annualSpend, startAge, endAge, inflation, returnRate) => {
  if (annualSpend <= 0 || endAge <= startAge) return 0
  // Excel PV(rate = investment return - inflation, type = 1).
  const rate = (returnRate - inflation) / 100
  const periods = endAge - startAge
  if (rate === 0) return annualSpend * periods
  return (annualSpend * (1 + rate) * (1 - (1 + rate) ** -periods)) / rate
}

const roundedReserve = (value) => Math.ceil(Math.max(5000000, value) / 2500000) * 2500000

const yearsToReachInflatedTarget = (target, monthlyInvestment, annualReturn, annualInflation) => {
  if (target <= 0) return 0
  if (monthlyInvestment <= 0) return null
  const monthlyReturn = (1 + annualReturn / 100) ** (1 / 12) - 1
  const monthlyInflation = (1 + annualInflation / 100) ** (1 / 12) - 1
  for (let months = 1; months <= 12 * 80; months += 1) {
    const invested =
      monthlyReturn <= 0
        ? monthlyInvestment * months
        : (monthlyInvestment * ((1 + monthlyReturn) ** months - 1)) / monthlyReturn
    const required = target * (1 + monthlyInflation) ** months
    if (invested >= required) return months / 12
  }
  return null
}

const compoundedMonthlyInvestment = (monthlyInvestment, years, annualReturn) => {
  if (monthlyInvestment <= 0 || years <= 0) return 0
  const months = years * 12
  const monthlyReturn = (1 + annualReturn / 100) ** (1 / 12) - 1
  if (monthlyReturn <= 0) return monthlyInvestment * months
  return (monthlyInvestment * ((1 + monthlyReturn) ** months - 1)) / monthlyReturn
}

const inflatedAmount = (amount, years, annualInflation) =>
  amount * (1 + annualInflation / 100) ** years

const gapProjection = (target, monthlyInvestment, annualReturn, annualInflation, closureYears) => {
  if (target <= 0) return [{ year: 0, requiredAmount: 0, investedAmount: 0, progressPercent: 100 }]
  if (monthlyInvestment <= 0)
    return [{ year: 0, requiredAmount: target, investedAmount: 0, progressPercent: 0 }]
  const horizon = Math.max(1, Math.min(80, Math.ceil(closureYears || 10)))
  const years = Array.from({ length: horizon + 1 }, (_, index) => index)
  if (closureYears && !Number.isInteger(closureYears)) years.push(closureYears)
  return [...new Set(years)]
    .sort((a, b) => a - b)
    .map((projectionYear) => {
      const requiredAmount = inflatedAmount(target, projectionYear, annualInflation)
      const investedAmount = Math.min(
        requiredAmount,
        compoundedMonthlyInvestment(monthlyInvestment, projectionYear, annualReturn),
      )
      return {
        year: Math.round(projectionYear * 10) / 10,
        requiredAmount,
        investedAmount,
        progressPercent: requiredAmount
          ? Math.min(100, (investedAmount / requiredAmount) * 100)
          : 100,
      }
    })
}

/**
 * A first pass using the verified Excel annuity-due formulas and explicit reserve inputs.
 * It is kept pure so the same estimate can be rendered on the client and tested independently.
 */
export function calculateFirstPass(planner, today = new Date()) {
  const assumptions = {
    planTill: numberValue(planner.assumptions?.planTill) || 90,
    essentialInflation: numberValue(planner.assumptions?.essentialInflation) || 6,
    nonEssentialInflation: numberValue(planner.assumptions?.nonEssentialInflation) || 6,
    educationInflation: numberValue(planner.assumptions?.educationInflation) || 8,
    realEstateInflation: numberValue(planner.assumptions?.realEstateInflation) || 5,
    hybridReturn: 10,
    gapInvestmentReturn: 11,
  }
  const year = today.getFullYear()
  const planningMembers = (planner.members || [])
    .filter((member) => ['Self', 'Spouse / Partner'].includes(member.relationship))
    .map((member) => ({ ...member, age: ageFromDob(member.dob, today) }))
    .filter((member) => member.age !== null)
    .sort((a, b) => a.age - b.age)
  const currentAge = planningMembers.length ? planningMembers[0].age : 33
  const monthlyGross = (planner.salaries || []).reduce(
    (sum, income) => sum + numberValue(income.gross),
    0,
  )
  const monthlyDeductions = (planner.salaries || [])
    .filter((income) => income.incomeType === 'Salary')
    .reduce(
      (sum, income) =>
        sum + numberValue(income.tax) + numberValue(income.pf) + numberValue(income.nps),
      0,
    )
  const monthlyNetIncome = Math.max(0, monthlyGross - monthlyDeductions)
  const housingEmi = numberValue(planner.monthlyExpenses?.emi)
  const monthlyLoanEmiTotal = (planner.loans || []).reduce((sum, loan) => {
    if (loan.type === 'Home loan' && housingEmi > 0) return sum
    return sum + numberValue(loan.emi)
  }, 0)
  const monthlyExpenseTotal =
    Object.values(planner.monthlyExpenses || {}).reduce(
      (sum, value) => sum + numberValue(value),
      0,
    ) + monthlyLoanEmiTotal
  const retirementInput = retirementStreams(planner)
  const annualExpenseTotal = retirementInput.annualExpenseTotal
  const monthlySurplus = Math.max(
    0,
    monthlyNetIncome - monthlyExpenseTotal - annualExpenseTotal / 12,
  )

  // Match the source planner: retirement is not one blended monthly number.
  // It funds three independently inflated streams: core monthly expenses and
  // rent, holidays, and the rest of the applicable annual expenses.
  const {
    parentAnnualExpense,
    basicCurrent: retirementBasicCurrent,
    basicIncrease: retirementBasicIncrease,
    basicPlanned: retirementBasicPlanned,
    vacationCurrent: retirementVacationCurrent,
    vacationIncrease: retirementVacationIncrease,
    vacationPlanned: retirementVacationPlanned,
    annualCurrent: retirementAnnualCurrent,
    annualIncrease: retirementAnnualIncrease,
    annualPlanned: retirementAnnualPlanned,
  } = retirementInput
  const retirementBasicCorpus = corpusForRecurringCost(
    retirementBasicPlanned * 12,
    currentAge,
    assumptions.planTill,
    assumptions.essentialInflation,
    assumptions.hybridReturn,
  )
  const retirementVacationCorpus = corpusForRecurringCost(
    retirementVacationPlanned,
    currentAge,
    assumptions.planTill,
    assumptions.nonEssentialInflation,
    assumptions.hybridReturn,
  )
  const retirementAnnualCorpus = corpusForRecurringCost(
    retirementAnnualPlanned,
    currentAge,
    assumptions.planTill,
    assumptions.nonEssentialInflation,
    assumptions.hybridReturn,
  )
  const retirementCorpus = retirementBasicCorpus + retirementVacationCorpus + retirementAnnualCorpus
  const goalFunding = (planner.assetGoals || [])
    .filter((goal) => numberValue(goal.amount) > 0)
    .map((goal) => {
      const years = Math.max(0, numberValue(goal.year) - year)
      const futureValue =
        numberValue(goal.amount) *
        (1 +
          (goal.kind === 'Home'
            ? assumptions.realEstateInflation
            : assumptions.nonEssentialInflation) /
            100) **
          years
      return {
        name: goal.name || goal.kind || 'One-time goal',
        year: numberValue(goal.year) || year,
        presentValue: futureValue / (1 + assumptions.hybridReturn / 100) ** years,
      }
    })
  const goalCorpus = goalFunding.reduce((sum, goal) => sum + goal.presentValue, 0)

  const childCorpus = (planner.members || [])
    .filter((member) => member.relationship === 'Child')
    .reduce((sum, member) => {
      const age = ageFromDob(member.dob, today)
      const plan = childPlanWithDefaults(planner.childPlans?.[member.id], member.id, planner, age)
      return sum + corpusForChildEducation(plan, age)
    }, 0)
  const childWeddingCorpus = (planner.members || [])
    .filter((member) => member.relationship === 'Child')
    .reduce((sum, member) => {
      const age = ageFromDob(member.dob, today)
      const plan = childPlanWithDefaults(planner.childPlans?.[member.id], member.id, planner, age)
      return sum + corpusForChildWedding(plan, age)
    }, 0)
  const hasParentInFamily = (planner.members || []).some(
    (member) => member.relationship === 'Parent',
  )
  const parentSpend = hasParentInFamily
    ? numberValue(planner.monthlyExpenses?.parents) * 12 + parentAnnualExpense
    : 0
  const parentCorpus = corpusForRecurringCost(
    parentSpend,
    Math.min(
      ...(planner.members || [])
        .filter((member) => member.relationship === 'Parent')
        .map((member) => ageFromDob(member.dob, today))
        .filter((age) => age !== null),
      assumptions.planTill,
    ),
    assumptions.planTill,
    assumptions.essentialInflation,
    assumptions.hybridReturn,
  )
  const baseRequirement =
    retirementCorpus + goalCorpus + childCorpus + childWeddingCorpus + parentCorpus
  const reserveInput = (key, fallback) =>
    hasValue(planner.assumptions?.[key])
      ? Math.max(0, numberValue(planner.assumptions[key]))
      : fallback
  const emergencyReserve = reserveInput(
    'emergencyReserve',
    Math.round((retirementBasicPlanned * 6) / 100000) * 100000,
  )
  const contingencyReserve = reserveInput(
    'contingencyReserve',
    roundedReserve(baseRequirement * 0.1),
  )
  const assetReplacementReserve = reserveInput(
    'assetReplacementReserve',
    roundedReserve(baseRequirement * 0.1),
  )
  const totalRequiredToday =
    baseRequirement + emergencyReserve + contingencyReserve + assetReplacementReserve
  const holdings = (planner.holdings || []).reduce(
    (sum, holding) => sum + numberValue(holding.value),
    0,
  )
  const homeValue = (planner.holdings || [])
    .filter((holding) => holding.name === 'Home')
    .reduce((sum, holding) => sum + numberValue(holding.value), 0)
  const realAssetValue = (planner.holdings || [])
    .filter(
      (holding) =>
        holding.category === 'Real asset' ||
        /^(home|land|gold(?: assets| \(.*\))?|flat|apartment|jewellery)$/i.test(
          (holding.name || '').trim(),
        ),
    )
    .reduce((sum, holding) => sum + numberValue(holding.value), 0)
  const loans = (planner.loans || []).reduce((sum, loan) => sum + numberValue(loan.amount), 0)
  const totalNetWorth = holdings - loans
  const netWorthWithoutHome = totalNetWorth - homeValue
  // The source uses liquid net worth as the default contribution to the plan:
  // property, land and gold may be part of net worth but are not assumed to
  // be immediately available to fund a financial-freedom target.
  const liquidNetWorth = totalNetWorth - realAssetValue
  const currentNetWorth = liquidNetWorth
  const gapToday = Math.max(0, totalRequiredToday - liquidNetWorth)
  const gapClosureYears = yearsToReachInflatedTarget(
    gapToday,
    monthlySurplus,
    assumptions.gapInvestmentReturn,
    assumptions.essentialInflation,
  )
  const projection = gapProjection(
    gapToday,
    monthlySurplus,
    assumptions.gapInvestmentReturn,
    assumptions.essentialInflation,
    gapClosureYears,
  )
  const gapClosureMonths =
    gapClosureYears === null ? null : Math.max(0, Math.ceil(gapClosureYears * 12))
  const gapClosureDate =
    gapClosureMonths === null
      ? null
      : new Date(today.getFullYear(), today.getMonth() + gapClosureMonths, today.getDate())
  const gapClosureAge =
    gapClosureDate === null
      ? null
      : planningMembers[0]?.dob
      ? ageFromDob(planningMembers[0].dob, gapClosureDate)
      : Math.floor(currentAge + gapClosureMonths / 12)
  const futureGapTarget =
    gapClosureYears === null
      ? null
      : inflatedAmount(gapToday, gapClosureYears, assumptions.essentialInflation)
  const breakdown = [
    {
      key: 'retirement-monthly',
      label: 'Monthly retirement expenses',
      value: retirementBasicCorpus,
    },
    {
      key: 'retirement-annual',
      label: 'Annual retirement expenses',
      value: retirementAnnualCorpus,
    },
    { key: 'retirement-vacation', label: 'Retirement travel', value: retirementVacationCorpus },
    ...(goalCorpus ? [{ key: 'goals', label: 'One-time goals', value: goalCorpus }] : []),
    ...(childCorpus
      ? [{ key: 'children', label: 'Children’s education', value: childCorpus }]
      : []),
    ...(childWeddingCorpus
      ? [{ key: 'weddings', label: 'Children’s weddings', value: childWeddingCorpus }]
      : []),
    ...(parentCorpus ? [{ key: 'parents', label: 'Parent support', value: parentCorpus }] : []),
    { key: 'emergency', label: 'Emergency reserve', value: emergencyReserve },
    { key: 'contingency', label: 'Contingency reserve', value: contingencyReserve },
    { key: 'replacement', label: 'Asset replacement reserve', value: assetReplacementReserve },
  ]
  return {
    currentAge,
    monthlyGross,
    monthlyDeductions,
    monthlyNetIncome,
    monthlyExpenseTotal,
    annualExpenseTotal,
    monthlySurplus,
    monthlyLoanEmiTotal,
    retirementMonthly: retirementBasicPlanned,
    retirementAnnualExpenseTotal: retirementVacationPlanned + retirementAnnualPlanned,
    retirementAnnualExpenseMonthly: (retirementVacationPlanned + retirementAnnualPlanned) / 12,
    retirementMonthlyModelled: retirementBasicPlanned,
    retirementBasicCurrent,
    retirementBasicIncrease,
    retirementBasicPlanned,
    retirementVacationCurrent,
    retirementVacationIncrease,
    retirementVacationPlanned,
    retirementAnnualCurrent,
    retirementAnnualIncrease,
    retirementAnnualPlanned,
    retirementBasicCorpus,
    retirementVacationCorpus,
    retirementAnnualCorpus,
    retirementCorpus,
    goalFunding,
    goalCorpus,
    childCorpus,
    childWeddingCorpus,
    parentCorpus,
    emergencyReserve,
    contingencyReserve,
    assetReplacementReserve,
    baseRequirement,
    totalRequiredToday,
    holdings,
    homeValue,
    realAssetValue,
    loans,
    totalNetWorth,
    netWorthWithoutHome,
    liquidNetWorth,
    currentNetWorth,
    gapToday,
    futureGapTarget,
    gapClosureYears,
    gapClosureMonths,
    gapClosureYear: gapClosureDate?.getFullYear() || null,
    gapClosureMonth: gapClosureDate ? monthYear(gapClosureDate) : null,
    gapClosureAge,
    gapProjection: projection,
    breakdown,
    assumptions,
  }
}
// Keep earlier answers, but don't reuse a numeric position after changing the question sequence.
export function restorePlanner(saved) {
  const base = createPlanner()
  const next = { ...base, ...saved }
  for (const section of [
    'monthlyExpenses',
    'yearlyExpenses',
    'insurance',
    'retirement',
    'assumptions',
    'answers',
  ])
    next[section] = { ...base[section], ...saved[section] }
  next.childPlans = { ...base.childPlans, ...(saved.childPlans || {}) }
  // Upgrade old implicit 5% defaults when loading a saved questionnaire.
  // Rates explicitly changed away from the old default remain intact.
  if (!saved.assumptions?.inflationVersion) {
    for (const key of ['essentialInflation', 'nonEssentialInflation']) {
      if (!hasValue(saved.assumptions?.[key]) || Number(saved.assumptions[key]) === 5)
        next.assumptions[key] = 6
    }
    next.childPlans = Object.fromEntries(
      Object.entries(next.childPlans).map(([id, plan]) => [
        id,
        {
          ...plan,
          weddingInflation:
            !hasValue(plan.weddingInflation) || Number(plan.weddingInflation) === 5
              ? 6
              : plan.weddingInflation,
        },
      ]),
    )
  }
  // These are automatic assumptions, not questionnaire answers.
  for (const key of ['basicIncrease', 'monthlyIncrease', 'vacationIncrease', 'annualIncrease'])
    next.retirement[key] = 0
  for (const key of ['emergencyReserve', 'contingencyReserve', 'assetReplacementReserve'])
    next.assumptions[key] = ''
  next.assumptions.inflationVersion = 2
  for (const section of [
    'members',
    'salaries',
    'holdings',
    'loans',
    'assetGoals',
    'extraYearlyExpenses',
  ])
    next[section] = Array.isArray(saved[section]) ? saved[section] : base[section]
  if (!next.members.length) next.members = base.members
  if (saved.questionnaire2Version !== 2) {
    next.questionnaire2Step = 'welcome'
    next.annualSelected = ANNUAL_COSTS.filter(([key]) => hasValue(next.yearlyExpenses[key])).map(
      ([key]) => key,
    )
    next.annualChoice = next.annualSelected.length ? 'Selected' : ''
    // Old rent values combined rent and EMI; ask the user to classify the amount.
    if (hasValue(next.monthlyExpenses.rent)) next.legacyHousingAmount = next.monthlyExpenses.rent
    next.monthlyExpenses.rent = ''
    next.monthlyExpenses.housing = ''
  }
  next.questionnaire2Version = 2
  return next
}
export function cleanPlanner(planner, today = new Date()) {
  // Saving and loading must use the same versioned defaults.
  planner = restorePlanner(planner)
  const next = {
    ...planner,
    salaries: planner.salaries.map((income) =>
      income.incomeType === 'Salary' ? income : { ...income, tax: '', pf: '', nps: '' },
    ),
  }
  next.monthlyExpenses = {
    ...planner.monthlyExpenses,
    rent: ['Rent', 'Both'].includes(planner.monthlyExpenses.housing)
      ? planner.monthlyExpenses.rent
      : '',
    emi: ['Home-loan EMI', 'Both'].includes(planner.monthlyExpenses.housing)
      ? planner.monthlyExpenses.emi
      : '',
    parents: planner.parentsSupport === 'Neither' ? '' : planner.monthlyExpenses.parents,
    children: planner.monthlyExpenses.children,
  }
  next.loans = (planner.loans || []).filter(
    (loan) => hasValue(loan.amount) || hasValue(loan.emi) || hasValue(loan.monthsLeft),
  )
  next.insurance = { ...planner.insurance }
  for (const [answer, prefix] of [
    ['corporateMedical', 'corporate'],
    ['privateMedical', 'private'],
    ['term', 'term'],
  ]) {
    if (next.insurance[answer] !== 'Yes')
      for (const suffix of ['Coverage', 'Who', 'Basis']) next.insurance[`${prefix}${suffix}`] = ''
  }
  next.yearlyExpenses = { ...planner.yearlyExpenses }
  const applicableKeys = applicableAnnualCosts(planner).map(([key]) => key)
  next.annualSelected = planner.annualSelected.filter((key) => applicableKeys.includes(key))
  for (const [key] of ANNUAL_COSTS) {
    if (!next.annualSelected.includes(key)) next.yearlyExpenses[key] = ''
  }
  const childIds = new Set(
    (planner.members || [])
      .filter((member) => member.relationship === 'Child')
      .map((member) => member.id),
  )
  next.childPlans = Object.fromEntries(
    Object.entries(planner.childPlans || {})
      .filter(([childId]) => childIds.has(childId))
      .map(([childId, plan]) => [childId, childPlanWithDefaults(plan, childId, planner)]),
  )
  next.estimates = calculateFirstPass(next, today)
  return next
}

export const applicableAnnualCosts = (planner) =>
  ANNUAL_COSTS.filter(
    ([key]) =>
      key !== 'childrenFees' || planner.members.some((member) => member.relationship === 'Child'),
  )
